/**
 * Institutional Demo Portal — synthetic data only (Iteration 21C-C).
 *
 * Never touches production data. Backend demo seed endpoints refuse
 * outside sandbox with the three-key gate
 * (VLK_RUNTIME_MODE=sandbox + MOCK_FLAG=true + VLK_DEMO_SEED_ENABLED=true).
 */
import React from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import { DemoRoute } from "./PortalGuards";
import DemoWorkflow from "./pages/DemoWorkflow";
import VeraLokLogo from "@/marketing/VeraLokLogo";
import { PortalHeaderUserMenu } from "./PortalHeaderUserMenu";
import "@/marketing/design-system.css";

const SECTIONS = [
  { key: "workflow", label: "Guided Workflow", path: "" },
  { key: "about", label: "About the Demo", path: "about" },
];

export default function DemoShell() {
  return (
    <DemoRoute>
      <div className="vlk-portal min-h-screen flex flex-col" data-testid="demo-shell">
        <DemoBanner />
        <div className="flex flex-1 min-h-0">
          <DemoSidebar />
          <main className="flex-1 min-w-0">
            <div className="p-6">
              <Routes>
                <Route path="" element={<DemoWorkflow />} />
                <Route path="about" element={<DemoAbout />} />
                <Route path="*" element={<Navigate to="" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </DemoRoute>
  );
}

function DemoBanner() {
  return (
    <div className="vlk-portal-banner-demo px-6 py-3 flex items-center justify-between"
         data-testid="demo-banner">
      <div className="flex items-center gap-3 text-xs font-semibold tracking-[0.2em] uppercase">
        <VeraLokLogo size={20} showWordmark={false} />
        <span>Demo · Sandbox · Synthetic data only</span>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-yellow-300/90">
        <span className="hidden md:inline">Nothing here touches production</span>
        <PortalHeaderUserMenu variant="institution" testIdPrefix="demo" />
      </div>
    </div>
  );
}

function DemoSidebar() {
  return (
    <aside className="vlk-portal-sidebar w-60 p-4 space-y-1 shrink-0" data-testid="demo-sidebar">
      <div className="mb-4 flex items-center gap-2">
        <VeraLokLogo size={24} />
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 text-white/40">
        Demo Portal
      </div>
      {SECTIONS.map((s) => (
        <NavLink
          key={s.key}
          to={`/demo-portal/${s.path}`}
          end={s.path === ""}
          className={({ isActive }) =>
            `vlk-portal-nav-link ${isActive ? "active" : ""}`
          }
          data-testid={`demo-nav-${s.key}`}
        >
          {s.label}
        </NavLink>
      ))}
    </aside>
  );
}

function DemoAbout() {
  return (
    <div className="space-y-4 max-w-2xl" data-testid="demo-about">
      <h1 className="text-2xl font-semibold">About the VeraLok Demo</h1>
      <p className="text-sm text-white/70 leading-relaxed">
        A guided evaluation of VeraLok's Zero-Knowledge trust verification flow using
        synthetic identities, institutions, and consents. Demo endpoints are strictly
        confined to the sandbox environment — production data is never touched.
      </p>
      <div className="vlk-glass p-4 text-xs">
        <div className="uppercase tracking-widest text-white/50 mb-2">Isolation guarantees</div>
        <ul className="space-y-1 list-disc list-inside text-white/70">
          <li>Synthetic identities only — no real credentials of any kind.</li>
          <li>Synthetic institutions (Bank, Government, Health).</li>
          <li>Pre-seeded consents covering the full lifecycle (Pending, Granted, Denied, Revoked, Expired).</li>
          <li>Every demo write is tagged and scoped to the sandbox seeder.</li>
          <li>Demo endpoints refuse to execute outside a strictly sandboxed environment.</li>
        </ul>
      </div>
      <div className="vlk-glass p-4 text-xs">
        <div className="uppercase tracking-widest text-white/50 mb-2">Workflow covered</div>
        <ol className="space-y-1 list-decimal list-inside text-white/70">
          <li>Institution sign-in (authenticated demo session)</li>
          <li>QR verification (single-use, short-lived, cryptographically signed)</li>
          <li>Consent request (scoped and purpose-bound)</li>
          <li>Citizen decision (explicit grant or deny — no defaults)</li>
          <li>Trust event (bound to a granted consent, with an audit reference)</li>
          <li>Audit trail (cryptographically linked, tamper-evident)</li>
          <li>Analytics (denial rate and risk distribution)</li>
        </ol>
      </div>
    </div>
  );
}
